/************************************************************************************************
 * Copyright TRUSST AI PTY LTD. All Rights Reserved.                                            *
 *                                                                                              *
 * Licensed under the TRUSST SOFTWARE LICENSE (the "License"). You may not use this file except *
 * in compliance with the "LICENSE" file accompanying this file. This file is distributed       *
 * on an "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, express or implied.       *
 *                                                                                              *
 * See the "License" file for the specific language governing permissions and limitations       *
 * under the License and limitations under the License.                                         *
 ***********************************************************************************************/

import {useMediaQuery} from '@mui/material';
import Box from '@mui/material/Box';
import GlobalStyles from '@mui/material/GlobalStyles';
import * as React from 'react';
import {Outlet} from 'react-router-dom';

import {SideNav} from './side-nav';
import {TopNav} from './top-nav';
import {layoutConfig} from '../config';

const SIDE_NAV_WIDTH = 250;
const SIDE_NAV_WIDTH_COMPACT = 85;

export function DashboardLayout(): React.JSX.Element {
  const isSmallScreen = useMediaQuery('(max-width:1024px)');
  const [compactSideNav, setCompactSideNav] = React.useState(isSmallScreen);

  return (
    <React.Fragment>
      <GlobalStyles
        styles={{
          body: {
            '--MainNav-height': '56px',
            '--MainNav-zIndex': 1000,
            '--SideNav-width': compactSideNav ? `${SIDE_NAV_WIDTH_COMPACT}px` : `${SIDE_NAV_WIDTH}px`,
            '--SideNav-zIndex': 1100,
            '--MobileNav-width': '260px',
            '--MobileNav-zIndex': 1100,
          },
        }}
      />
      <Box
        sx={{
          bgcolor: 'var(--mui-palette-background-default)',
          display: 'flex',
          flexDirection: 'column',
          position: 'relative',
          minHeight: '100%',
        }}
      >
        <SideNav
          mainItems={layoutConfig.navMainItems}
          secondaryItems={layoutConfig.navSecondaryItems}
          compact={compactSideNav}
        />
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            flexDirection: 'column',
            pl: 'var(--SideNav-width)',
          }}
        >
          <TopNav compact={compactSideNav} setCompact={setCompactSideNav} />
          <Box
            component={'main'}
            sx={{
              '--Content-margin': '0 auto',
              '--Content-maxWidth': 'var(--maxWidth-xl)',
              '--Content-paddingX': 'calc(4 * var(--mui-spacing))',
              '--Content-paddingY': '64px',
              '--Content-padding': 'var(--Content-paddingY) var(--Content-paddingX)',
              '--Content-width': '100%',
              display: 'flex',
              flex: '1 1 auto',
              flexDirection: 'column',
            }}
          >
            <Box
              sx={{
                maxWidth: 'var(--Content-maxWidth)',
                m: 'var(--Content-margin)',
                p: 'var(--Content-padding)',
                width: 'var(--Content-width)',
              }}
            >
              <Outlet />
            </Box>
          </Box>
        </Box>
      </Box>
    </React.Fragment>
  );
}
